import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { NextRouter } from 'next/router';
import { getConfig } from '@chegg-web-tools/config-plugin';
import { OneAuth, OneAuthProps } from '@chegg/one-auth';
import { getCheggTldFrom, getLangPreferenceCookie } from '@chegg/utils';
import AdScript from '@components/AdScript/AdScript';
import { Footer } from '@components/Footer/Footer';
import { Header } from '@components/Header/Header';
import ValueProps from '@components/ValueProps/ValueProps';
import { ValuePropType } from '@components/ValueProps/types';
import { UniqueCheggProfile } from '@lib/constants';
import { getCountryByIp } from '@lib/geoLocation';
import { useClickstreamView, useClickstreamViewRio2 } from '@lib/hooks/useClickstreamView';
import { useDigitalData } from '@lib/hooks/useDigitalData';
import { initialProps } from '@lib/initialProps';
import { withPageRouter } from '@lib/withPageRouter';
import { getInitialView } from '@queryParams/getInitialView';
import { setCountry } from '@queryParams/setCountry';
import { setRedirectUrl } from '@queryParams/setRedirectUrl';
import { useFetchContentfulLayout } from '@src/hooks/useFetchContentfulLayout';
import { useOneAuthEvent } from '@src/hooks/useOneAuthEvent';
import type { Locale } from '@src/lib/contentful/types';
import {
  AuthPageContainer,
  CheggMathBackground,
  OneAuthContainer,
} from '@src/pageAssets/auth/styles';
import { initSdk } from '@utils/initSdk';
import { getBaseCheggUrl } from '@src/lib/getTldUrls';

initSdk();

export const AuthPage = (props: { router: NextRouter }) => {
  const {
    router: { query },
  } = props;
  const isTldFeaturesEnabled = getConfig('enableTldFeatures');
  const env = getConfig('env');
  const envBaseUrl = getConfig('envBaseUrl');
  const locale = getLangPreferenceCookie();

  const [valuePropsType, setValuePropsType] = useState<ValuePropType | false>();
  const [customRedirect, setCustomRedirect] = useState<URL | string>('');
  const [oneAuthProps, setOneAuthProps] = useState<OneAuthProps>({
    ...initialProps,
    view: getInitialView(props.router),
  });
  const [baseCheggUrl, setBaseCheggUrl] = useState('');

  // IDENTITY-2051: send auth page view event to rio2 for POC
  useClickstreamViewRio2();
  // send clickstream view
  useClickstreamView('auth page');
  // send adobe digital data stuff
  useDigitalData('auth');

  // sets the valueprops type
  useEffect(() => {
    setValuePropsType(query.type as ValuePropType);
  }, [query.type]);

  // sets custom url on successful auth
  useEffect(() => {
    setCustomRedirect(setRedirectUrl(query.redirect as string));
  }, [query.redirect]);

  // sets country on test env
  useEffect(() => {
    if (env !== 'prod' && query.country) {
      const country = setCountry(query.country as string);
      if (country) {
        setOneAuthProps((prevProps: OneAuthProps) => ({ ...prevProps, country }));
      }
    }
  }, [env, query.country]);

  // set oneAuth country by ip (test env only)
  useEffect(() => {
    if (env !== 'prod' && query.setip) {
      getCountryByIp(query.setip as string).then((country) => {
        setOneAuthProps((prevProps: OneAuthProps) => ({ ...prevProps, country }));
      });
    }
  }, [env, query.setip]);

  useEffect(() => {
    const forceMfaByApp = 'MFA_BY_APP' in query;
    const displayOpenPass = 'OPENPASS' in query;

    setOneAuthProps((prevProps) => {
      return {
        ...prevProps,
        options: {
          ...prevProps.options,
          legacyMfa: !forceMfaByApp,
          ...(displayOpenPass && {
            signin: {
              ssoProviders: [...(prevProps.options?.signin?.ssoProviders ?? []), 'openPass'],
            },
            signup: {
              form: {
                ...prevProps.options?.signup?.form,
                credentials: {
                  ssoProviders: [...(prevProps.options?.signin?.ssoProviders ?? []), 'openPass'],
                },
              },
            },
          }),
        },
      };
    });
  }, []);

  // set oneAuth language
  const layout = useFetchContentfulLayout(
    setOneAuthProps,
    valuePropsType,
    query as any,
    locale as Locale
  );

  // listen to oneAuth callback event
  const onOneAuthEvent = useOneAuthEvent(global.window, customRedirect, envBaseUrl);

  // set specific container class when valueProps window is rendered
  const classname = (valuePropsType && valuePropsType) || '';

  useEffect(() => {
    const url = window.location.origin;
    const targetTld = getCheggTldFrom(url);
    setBaseCheggUrl(getBaseCheggUrl({ url, envBaseUrl, isTldFeaturesEnabled, targetTld }));
  }, []);

  return (
    <>
      <Head>
        <title>Sign In or Sign Up | Chegg.com</title>
      </Head>
      <Header data-test="header" baseCheggUrl={baseCheggUrl} />
      <main>
        <AuthPageContainer>
          {renderBackground()}
          {renderOneAuth()}
        </AuthPageContainer>
        <Footer baseCheggUrl={baseCheggUrl} locale={locale} />
      </main>
    </>
  );

  function renderOneAuth() {
    return (
      <OneAuthContainer className={classname} data-test="one_auth_container">
        {renderValueProps()}
        {shouldShowOnboarding(oneAuthProps) ? (
          <OneAuth {...oneAuthProps} callback={onOneAuthEvent} className={classname} />
        ) : (
          renderOneAuthWithoutOnboarding(oneAuthProps, valuePropsType as ValuePropType)
        )}
      </OneAuthContainer>
    );
  }

  function shouldShowOnboarding(authProps: OneAuthProps) {
    return (
      authProps?.options?.signup?.showOnboardingQuestions ||
      (typeof window !== 'undefined' && window.location.search.indexOf('show_onboarding=true') > -1)
    );
  }
  function renderOneAuthWithoutOnboarding(props: OneAuthProps, valuePropsType: ValuePropType) {
    const propsWithoutOnboarding: OneAuthProps = {
      ...props,
      options: {
        ...props.options,
        signup: {
          ...props.options?.signup,
          form: { credentials: props.options?.signup?.form?.credentials, userInfo: undefined },
          isSimplifiedStudy: valuePropsType === UniqueCheggProfile.SIMPLIFIED_STUDY,
        },
      },
    };
    return <OneAuth {...propsWithoutOnboarding} callback={onOneAuthEvent} className={classname} />;
  }

  function renderBackground() {
    if (valuePropsType === 'simplifiedstudy') return null;
    if (valuePropsType === 'cheggmathpricing') {
      return <CheggMathBackground data-test="chegg_math_background" />;
    }
    return <AdScript />;
  }

  function renderValueProps() {
    if (!valuePropsType || !layout) return null;
    return <ValueProps type={valuePropsType} data={layout.valueProps} />;
  }
};

export default withPageRouter(AuthPage);
